import { USER_WORKER_URL } from '../../config.js';
import { storage } from '../../utils/storage.js';
import { HangoutState } from '../state/HangoutState.js';
import { isResolvedConversationId } from './request-builder.js';
import { fetchCache } from '../../utils/fetchCache.js';
import {
  DEFAULT_CONTEXT_TURNS,
  DEFAULT_MAX_TOKENS,
  DEFAULT_REASONING_EFFORT,
  DEFAULT_TEMPERATURE,
  REASONING_EFFORTS,
  getUserModelOverride
} from './constants.js';
import { CHAT_SUMMARY_MAX_LENGTH, getChatMemory, setChatMemory } from './hangout-memory-storage.js';

const userWorkerBase = USER_WORKER_URL.replace(/\/+$/, '');

const RESPONSE_STYLE_LOCAL_KEYS = [
  'selectedVibeId',
  'selectedTemperature',
  'selectedReasoningEffort',
  'selectedMaxTokens',
  'selectedContextTurns',
  'selectedTopP',
  'selectedFrequencyPenalty',
  'selectedPresencePenalty'
];

const getAuthHeaders = async () => {
  const authToken = await window.tokenManager?.getValidAccessToken?.() ?? '';
  return {
    'Content-Type': 'application/json',
    ...(authToken ? { Authorization: `Bearer ${authToken}` } : {})
  };
};

const hasAuthToken = async () => Boolean(await window.tokenManager?.getValidAccessToken?.());

const hasLocalValue = (key) => {
  const value = storage.get(key);
  return value !== null && value !== undefined && value !== '';
};

const nearlyEqual = (a, b) => Math.abs(Number(a) - Number(b)) < 1e-6;

export const buildResponseStylePersistPayload = () => {
  const promptRaw = storage.get('selectedVibeId');
  const promptId = Number.parseInt(String(promptRaw ?? ''), 10);

  const temperatureRaw = storage.get('selectedTemperature');
  const temperature = typeof temperatureRaw === 'number' ? temperatureRaw : parseFloat(temperatureRaw);

  const reasoning = storage.get('selectedReasoningEffort');

  const maxTokensRaw = storage.get('selectedMaxTokens');
  const maxTokens = Number.parseInt(String(maxTokensRaw ?? ''), 10);

  const contextTurnsRaw = storage.get('selectedContextTurns');
  const contextTurns = Number.parseInt(String(contextTurnsRaw ?? ''), 10);

  const topPRaw = storage.get('selectedTopP');
  const topP = typeof topPRaw === 'number' ? topPRaw : parseFloat(topPRaw);

  const frequencyRaw = storage.get('selectedFrequencyPenalty');
  const frequencyPenalty = typeof frequencyRaw === 'number' ? frequencyRaw : parseFloat(frequencyRaw);

  const presenceRaw = storage.get('selectedPresencePenalty');
  const presencePenalty = typeof presenceRaw === 'number' ? presenceRaw : parseFloat(presenceRaw);

  return {
    prompt_id: Number.isFinite(promptId) ? promptId : null,
    temperature: Number.isFinite(temperature) && !nearlyEqual(temperature, DEFAULT_TEMPERATURE) ? temperature : null,
    reasoning: (typeof reasoning === 'string' && REASONING_EFFORTS.includes(reasoning) && reasoning !== DEFAULT_REASONING_EFFORT)
      ? reasoning
      : null,
    max_tokens: Number.isFinite(maxTokens) && maxTokens !== DEFAULT_MAX_TOKENS ? maxTokens : null,
    context_turns: Number.isFinite(contextTurns) && contextTurns !== DEFAULT_CONTEXT_TURNS ? contextTurns : null,
    top_p: Number.isFinite(topP) && topP > 0 ? topP : null,
    frequency_penalty: Number.isFinite(frequencyPenalty) && frequencyPenalty > 0 ? frequencyPenalty : null,
    presence_penalty: Number.isFinite(presencePenalty) && presencePenalty > 0 ? presencePenalty : null,
    model_override: getUserModelOverride()
  };
};

export const persistResponseStyleFromLocal = async () => {
  if (!(await hasAuthToken())) return;
  try {
    await fetchCache.fetchJSON(`${userWorkerBase}/response-style`, {
      method: 'PUT',
      headers: await getAuthHeaders(),
      body: JSON.stringify(buildResponseStylePersistPayload())
    });
  } catch (error) {
    console.warn('Failed to persist response style', error);
  }
};

let persistStyleTimer = null;

export const schedulePersistResponseStyle = () => {
  if (persistStyleTimer) clearTimeout(persistStyleTimer);
  persistStyleTimer = setTimeout(() => {
    persistStyleTimer = null;
    persistResponseStyleFromLocal();
  }, 400);
};

export const hydrateUserResponseStyleFromDb = async () => {
  if (!(await hasAuthToken())) return;
  const modelOverrideUnset = storage.get('selectedModelOverride') == null;
  const needsHydrate = modelOverrideUnset || RESPONSE_STYLE_LOCAL_KEYS.some((key) => !hasLocalValue(key));
  if (!needsHydrate) return;
  try {
    const data = await fetchCache.fetchJSON(`${userWorkerBase}/response-style`, {
      method: 'GET',
      headers: await getAuthHeaders()
    });
    const style = data?.style;
    if (storage.get('selectedModelOverride') == null) {
      const override = typeof style?.model_override === 'string' ? style.model_override.trim() : '';
      storage.set('selectedModelOverride', override);
    }
    if (!style || typeof style !== 'object') return;

    if (!hasLocalValue('selectedVibeId') && style.prompt_id != null) {
      storage.set('selectedVibeId', Number(style.prompt_id));
    }
    if (!hasLocalValue('selectedTemperature') && style.temperature != null) {
      storage.set('selectedTemperature', Number(style.temperature));
    }
    if (!hasLocalValue('selectedReasoningEffort') && typeof style.reasoning === 'string') {
      storage.set('selectedReasoningEffort', style.reasoning);
    }
    if (!hasLocalValue('selectedMaxTokens') && style.max_tokens != null) {
      storage.set('selectedMaxTokens', Number(style.max_tokens));
    }
    if (!hasLocalValue('selectedContextTurns') && style.context_turns != null) {
      storage.set('selectedContextTurns', Number(style.context_turns));
    }
    if (!hasLocalValue('selectedTopP') && style.top_p != null) {
      storage.set('selectedTopP', Number(style.top_p));
    }
    if (!hasLocalValue('selectedFrequencyPenalty') && style.frequency_penalty != null) {
      storage.set('selectedFrequencyPenalty', Number(style.frequency_penalty));
    }
    if (!hasLocalValue('selectedPresencePenalty') && style.presence_penalty != null) {
      storage.set('selectedPresencePenalty', Number(style.presence_penalty));
    }
  } catch (error) {
    console.warn('Failed to hydrate response style', error);
  }
};

export const fetchCustomPrompts = async () => {
  if (!(await hasAuthToken())) return [];
  try {
    const data = await fetchCache.fetchJSON(`${userWorkerBase}/custom-prompts`, {
      method: 'GET',
      headers: await getAuthHeaders()
    });
    return Array.isArray(data?.prompts) ? data.prompts : [];
  } catch (error) {
    console.warn('Failed to fetch custom prompts', error);
    return [];
  }
};

export const createCustomPrompt = async (name, prompt) => {
  if (!(await hasAuthToken())) {
    const error = new Error('Sign in to save custom prompts');
    error.code = 'AUTH_REQUIRED';
    throw error;
  }
  try {
    const data = await fetchCache.fetchJSON(`${userWorkerBase}/custom-prompts`, {
      method: 'POST',
      headers: await getAuthHeaders(),
      body: JSON.stringify({ name, prompt })
    });
    if (data?.success === false) {
      const error = new Error(data?.error || 'Failed to save custom prompt');
      error.code = data?.error_code;
      throw error;
    }
    return data?.prompt || null;
  } catch (error) {
    if (error.code === 'AUTH_REQUIRED') throw error;
    const err = new Error(error.body?.error || error.message || 'Failed to save custom prompt');
    err.code = error.body?.error_code || error.code;
    throw err;
  }
};

const resolveConversationId = (explicitId) => {
  if (isResolvedConversationId(explicitId)) return explicitId.trim();
  const fromState = HangoutState.getInstance?.()?.getConversationId?.();
  if (isResolvedConversationId(fromState)) return fromState.trim();
  return null;
};

export const hydrateChatSummaryFromDb = async (conversationId) => {
  const id = resolveConversationId(conversationId);
  if (!id || !(await hasAuthToken())) return;
  let current = getChatMemory();
  if (current.conversationId && current.conversationId !== id) {
    current = setChatMemory({ text: '', summarizedCount: 0, conversationId: id });
  }
  if (current.conversationId === id && current.text) return;
  try {
    const data = await fetchCache.fetchJSON(
      `${userWorkerBase}/chat-summary?conversation_id=${encodeURIComponent(id)}`,
      { method: 'GET', headers: await getAuthHeaders() }
    );
    if (data?.success === false) return;
    const summary = typeof data?.summary === 'string' ? data.summary.trim().slice(0, CHAT_SUMMARY_MAX_LENGTH) : '';
    if (!summary) {
      if (!current.conversationId) setChatMemory({ conversationId: id });
      return;
    }
    setChatMemory({
      text: summary,
      conversationId: id,
      summarizedCount: current.conversationId === id ? current.summarizedCount : 0
    });
  } catch (error) {
    console.warn('Failed to hydrate chat summary', error);
  }
};

export const persistChatSummaryFromLocal = async (conversationId) => {
  const id = resolveConversationId(conversationId);
  if (!id || !(await hasAuthToken())) return;
  setChatMemory({ conversationId: id });
  const { text } = getChatMemory();
  try {
    await fetchCache.fetchJSON(`${userWorkerBase}/chat-summary`, {
      method: 'PUT',
      headers: await getAuthHeaders(),
      body: JSON.stringify({
        conversation_id: id,
        summary: text.slice(0, CHAT_SUMMARY_MAX_LENGTH)
      })
    });
  } catch (error) {
    console.warn('Failed to persist chat summary', error);
  }
};

let persistSummaryTimer = null;

export const schedulePersistChatSummary = (conversationId) => {
  if (persistSummaryTimer) clearTimeout(persistSummaryTimer);
  persistSummaryTimer = setTimeout(() => {
    persistSummaryTimer = null;
    persistChatSummaryFromLocal(conversationId);
  }, 500);
};
